import { Column, GridApi } from 'ag-grid-community'
import { Locale, Translation } from 'locale'
import { flow, isEqual, isNil, isUndefined, uniqBy } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { GridColumnCopySelection } from 'store/grid/gridTypes'
import { notNilFilter } from 'utils'
import { StandardGridColumnId } from 'view/components/datagrid'
import { ColumnCopySelectorSelection } from './ColumnCopySelectorComponent'

export const filterColumns = (cols: Column[]) =>
  cols.filter(
    col =>
      col.getColDef().headerName &&
      col.getColDef().headerName !== Locale.translate(Translation.TC_EMPTY_HEADER) &&
      col.getColId() !== StandardGridColumnId.GROUPING
  )

export const createSortColumns = (orderedColIds: string[]) => (cols: Column[]) => {
  const defaultColumns = orderedColIds.map(colId => cols.find(column => column.getColId() === colId)).filter(notNilFilter)
  const remainingColumns = cols.filter(column => !orderedColIds.includes(column.getColId()))
  return [...defaultColumns, ...remainingColumns]
}

export const getOriginalOrCurrentColumnId = (c: Column) => {
  // in the case of grouped columns we may want to use the original id which is not prefixed
  // prefixing happens when column groups are constructed and the ids are prefixed to prevent collision
  // eg counterparty view or curve grid
  return c.getColDef().cellRendererParams?.originalColId ?? c.getColId()
}

export const columnCopySelectionsToGroupedColumnCopySelectorSelectionsMapper = (
  columnSelections: GridColumnCopySelection[],
  columns: Column[]
) => {
  return uniqBy(
    columnSelections.reduce<ColumnCopySelectorSelection[]>((columnCopySelectorSelections, selection) => {
      const column = columns.find(col => col.getColId() === selection.columnId)

      if (!column) {
        return columnCopySelectorSelections
      }

      const { headerComponentParams, headerName } = column.getColDef()
      const delta = !isNil(headerComponentParams) && !isNil(headerName)

      return [
        ...columnCopySelectorSelections,
        {
          selectionId: selection.groupingId,
          isSelected: selection.isSelected,
          indent: !isUndefined(headerComponentParams && headerComponentParams.parent),
          delta,
          headerName: headerName ?? '',
          label: headerName ?? ''
        }
      ]
    }, []),
    groupedSelection => groupedSelection.selectionId
  )
}

export const useStableColumns = (gridApi: GridApi, sortColumns?: (columns: Column[]) => Column[]) => {
  const [cols, setColumns] = useState<Column[]>([])
  const previousColumnsRef = useRef<Column[]>([])
  const columnSortNoOp = useCallback((c: Column) => c, [])
  useEffect(() => {
    const sortedColumns = flow(sortColumns ?? columnSortNoOp, filterColumns)(gridApi ? gridApi.getColumns() ?? [] : [])
    if (!isEqual(sortedColumns, previousColumnsRef.current)) {
      previousColumnsRef.current = sortedColumns
      setColumns(sortedColumns)
    }
  })

  return cols
}
